import React from 'react';
import { Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StatusSwitches from './StatusSwitches';
import { Tooltip } from '@mui/material';
import { StatusFilter } from '../Filters/StatusFilter/StatusFilter';
import { useEstados } from '../../hooks/useEstados';
import { getMPStatusColor } from '../../utils/statusUtils';
import RefreshButton from '../RefreshButton/RefreshButton';
import MarkdownRenderer from '../../utils/markDownRenderer.';

interface FuentesResumenGeneral {
  ficha: boolean;
  archivos_incluidos: string[];
  archivos_sin_analizar: string[];
  total_archivos_db: number;
  total_archivos_enviados: number;
}

interface DetailProps {
  nombre: string;
  resumen_llm?: string | null;
  resumen?: string | null;
  resumen_general?: string | null;
  fuentes_resumen_general?: FuentesResumenGeneral | null;
  fecha_creacion?: string | null;
  estado_licitacion: string;
  codigo_externo: string;
  codigo_estado: number;
  puntaje?: number | null;
  fecha_cierre?: string | null;
  fecha_adjudicacion?: string | null;
  monto_estimado?: number | null;
  monto_estimado_string?: string | null;
  moneda?: string | null;
  puntaje_archivos?: number | null;
  resumen_archivos_llm?: string | null;
  requisitos_postulacion?: string | null;
  criterios_evaluacion?: string | null;
  resumen_procesado_filtro_palabras?: boolean | null;
  archivos_procesados?: boolean | null;
  resumen_procesado_filtro_llm?: boolean | null;
  postulado?: boolean | null;
  adjudicado?: boolean | null;
  fecha_postulacion?: string | null;
  onPostulacionChange?: (isPostulada: boolean) => void;
  onAdjudicacionChange?: (isAdjudicada: boolean) => void;
  onStatusChange?: (newStatus: string) => void;
  fetchDetail?: () => Promise<void>;
  entityType?: 'licitaciones' | 'compras-agiles';
  visita_terreno?: string | null;
}

const Detail: React.FC<DetailProps> = ({
  nombre,
  resumen_llm = '',
  resumen,
  resumen_general,
  fuentes_resumen_general = null,
  fecha_creacion,
  estado_licitacion,
  codigo_externo,
  codigo_estado,
  onStatusChange,
  puntaje = 0,
  puntaje_archivos = 0,
  resumen_archivos_llm = '',
  fecha_cierre = '',
  monto_estimado = null,
  monto_estimado_string = null,
  moneda = 'CLP',
  resumen_procesado_filtro_palabras = null,
  archivos_procesados = null,
  requisitos_postulacion = null,
  criterios_evaluacion = null,
  resumen_procesado_filtro_llm = null,
  postulado = false,
  adjudicado = false,
  fecha_postulacion = null,
  fecha_adjudicacion = null,
  onPostulacionChange,
  onAdjudicacionChange,
  fetchDetail,
  entityType = 'licitaciones',
  visita_terreno = null,
}) => {
  const { estadosMPInverso } = useEstados();

  const hayArchivosSinAnalizar =
    fuentes_resumen_general &&
    fuentes_resumen_general.archivos_sin_analizar &&
    fuentes_resumen_general.archivos_sin_analizar.length > 0;

  const archivosAnalizados =
    fuentes_resumen_general?.total_archivos_enviados ?? 0;


  
  const getCleanResumenGeneral = (content: string | null | undefined) => {
    if (!content) return '';
    try {
      // Intenta parsear si viene como string JSON
      const parsed = JSON.parse(content);
      let text = parsed.resumen || content;
      
      // Si el texto interno viene envuelto en bloques de markdown, los limpia
      if (typeof text === 'string') {
        text = text.replace(/^```markdown\s*/i, '').replace(/```\s*$/, '');
      }
      return text;
    } catch (e) {
      // Si no es un JSON válido, evalúa si el string directo tiene bloques
      let text = content;
      if (typeof text === 'string') {
        text = text.replace(/^```markdown\s*/i, '').replace(/```\s*$/, '');
      }
      return text;
    }
  };


  return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 15 },
      }}>
        <Box sx={{ flex: { md: '0 0 65%' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StatusFilter
              currentStatus={estado_licitacion}
              licitacionId={codigo_externo}
              onStatusChange={onStatusChange}
              entityType={entityType}
            />
            <Box sx={{ display: 'flex', mt: 1 }}>
                <Tooltip title="Resumen procesado filtro palabras" arrow>
                  <Box>
                    {resumen_procesado_filtro_palabras === true ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="error" fontSize="small" />
                    )}
                  </Box>
                </Tooltip>
                <Tooltip title="Resumen procesado filtro LLM" arrow>
                  <Box>
                    {resumen_procesado_filtro_llm === true ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="error" fontSize="small" />
                    )}
                  </Box>
                </Tooltip>
                <Tooltip title="Archivos procesados" arrow>
                  <Box>
                    {archivos_procesados === true ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="error" fontSize="small" />
                    )}
                  </Box>
                </Tooltip>
            </Box>
            <RefreshButton
              onRefresh={fetchDetail || (() => {})}
              tooltip="Actualizar detalle"
            />
          </Box>
          <Box sx={{
            typography: 'h1',
            fontWeight: 700,
            fontSize: { xs: '18px', sm: '26px', md: '32px' },
            lineHeight: '100%',
            letterSpacing: '0%',
            textTransform: 'uppercase',
            color: '#1976D2',
            mb: 2,
            mt: 3
          }}>
            {nombre}
          </Box>
          {resumen_general ? (
            <Box>
              {fuentes_resumen_general && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{
                    fontSize: '12px',
                    color: '#757575',
                    backgroundColor: '#F5F5F5',
                    borderRadius: '4px',
                    px: 1,
                    py: 0.5,
                  }}>
                    {archivosAnalizados > 0
                      ? `Análisis basado en ficha + ${archivosAnalizados} archivo(s) adjunto(s)`
                      : 'Análisis basado en ficha de licitación'}
                  </Box>
                  {hayArchivosSinAnalizar && (
                    <Tooltip
                      title={`Archivos sin analizar (no disponibles en disco): ${fuentes_resumen_general.archivos_sin_analizar.join(', ')}`}
                      arrow
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                        <WarningAmberIcon sx={{ fontSize: '16px', color: '#FF9800' }} />
                        <Box sx={{ fontSize: '11px', color: '#FF9800', ml: 0.5 }}>
                          {fuentes_resumen_general.archivos_sin_analizar.length} sin analizar
                        </Box>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              )}
              <Box sx={{
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '16px', md: '18px' },
                lineHeight: '32px',
                letterSpacing: '2%',
                color: '#585858',
                mt: 2,
              }}>
                <MarkdownRenderer content={getCleanResumenGeneral(resumen_general)} />
              </Box>
            </Box>
          ) : (
            <>
              {resumen && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{
                    fontWeight: 600,
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    color: '#1976D2',
                  }}>
                    Resumen de mercado público
                  </Box>
                  <Box sx={{
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '16px', md: '18px' },
                    lineHeight: '32px',
                    letterSpacing: '2%',
                    color: '#585858',
                  }}>
                    <MarkdownRenderer content={resumen} />
                  </Box>
                </Box>
              )}

              {resumen_llm && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{
                    fontWeight: 600,
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    color: '#1976D2',
                  }}>
                    Resumen LLM
                  </Box>
                  <Box sx={{
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '16px', md: '18px' },
                    lineHeight: '32px',
                    letterSpacing: '2%',
                    color: '#585858',
                  }}>
                    <MarkdownRenderer content={resumen_llm} />
                  </Box>
                </Box>
              )}

              {resumen_archivos_llm && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{
                    fontWeight: 600,
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    color: '#1976D2',
                  }}>
                    Resumen de Archivos
                  </Box>
                  <Box sx={{
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '16px', md: '18px' },
                    lineHeight: '32px',
                    letterSpacing: '2%',
                    color: '#585858',
                  }}>
                    <MarkdownRenderer content={resumen_archivos_llm} />
                  </Box>
                </Box>
              )}
            </>
          )}

          {requisitos_postulacion && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '18px', md: '20px' },
                color: '#1976D2',
              }}>
                Documentos requeridos para postular
              </Box>
              <Box sx={{
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '16px', md: '18px' },
                lineHeight: '32px',
                letterSpacing: '2%',
                color: '#585858',
                mt: 1,
              }}>
                <MarkdownRenderer content={requisitos_postulacion} />
              </Box>
            </Box>
          )}

          {criterios_evaluacion && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '18px', md: '20px' },
                color: '#1976D2',
                mb: 1
              }}>
                Criterios de evaluación
              </Box>
              <Box sx={{
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    {criterios_evaluacion.split('\n').slice(0, 1).map((headerLine, idx) => (
                      <tr key={idx} style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E0E0E0' }}>
                        {headerLine.split('\t').map((col, cIdx) => (
                          <th key={cIdx} style={{ padding: '12px 16px', fontWeight: 600, fontSize: '14px', color: '#333' }}>
                            {col.trim()}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {criterios_evaluacion.split('\n').slice(1).map((rowLine, rIdx) => {
                      const cols = rowLine.split('\t');
                      if (cols.length === 1 && !cols[0].trim()) return null;
                      return (
                        <tr key={rIdx} style={{ borderBottom: '1px solid #E0E0E0' }}>
                          {cols.map((col, cIdx) => (
                            <td key={cIdx} style={{ padding: '12px 16px', fontSize: '14px', color: '#585858' }}>
                              {col.trim()}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </Box>
        
        <Box sx={{ flex: { md: '0 0 30%' }, borderRadius: '8px', border: '1px solid #E0E0E0', padding: '40px'}}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            '& h3': {
              fontWeight: 700,
              fontSize: { xs: '14px', sm: '16px', md: '18px' },
              lineHeight: '100%',
              letterSpacing: '0%',
              marginTop: 0,
              marginBottom: 1
            },
            '& p': {
              fontWeight: 400,
              fontSize: { xs: '12px', sm: '14px', md: '16px' },
              lineHeight: '100%',
              letterSpacing: '0%',
              marginTop: 0,
              marginBottom: 0
            }
          }}>

            <StatusSwitches
              isPostulada={postulado}
              isAdjudicada={adjudicado}
              fechaPostulacion={fecha_postulacion}
              fechaAdjudicacion={fecha_adjudicacion}
              onPostulacionChange={onPostulacionChange}
              onAdjudicacionChange={onAdjudicacionChange}
            />
            <Box>
              <h3>Código externo</h3>
              <p>{codigo_externo}</p>
            </Box>
            <Box>
              <h3>Fecha de apertura</h3>
              <p>
                {fecha_creacion
                  ? new Date(fecha_creacion).toLocaleDateString('es-CL', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'No disponible'}
              </p>
            </Box>

            <Box>
              <h3>Fecha de cierre</h3>
              <p>
                {fecha_cierre ? new Date(fecha_cierre).toLocaleDateString('es-CL', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'No especificada'}
              </p>
            </Box>

            {entityType === 'licitaciones' && (
              <Box>
                <h3>Monto estimado</h3>
                <p>
                  {monto_estimado ? `$${monto_estimado.toLocaleString('es-CL')} ${moneda}` : 'No especificado'}
                </p>
              </Box>
            )}

            {entityType === 'compras-agiles' && (
              <Box>
                <h3>Monto estimado</h3>
                <p>
                  {monto_estimado_string ? `$${parseFloat(monto_estimado_string.replace(/,/g, '')).toLocaleString('es-CL')} ${moneda}` : 'No especificado'}
                </p>
              </Box>
            )}
            <Box>
              <h3>Estado actual</h3>
              <p style={{
                color: Number(estado_licitacion) === 4
                  ? '#9e9e9e'
                  : getMPStatusColor(estadosMPInverso[Number(codigo_estado)] || ''),
                fontWeight: 700
              }}>
                {Number(estado_licitacion) === 4
                  ? 'Sin seguimiento'
                  : (estadosMPInverso[Number(codigo_estado)] || '')
                }
              </p>
            </Box>
            <Box>
              <h3>Puntaje IA por resumen</h3>
              <p>{puntaje ? `${puntaje} / 10` : 'Sin analizar'}</p>
            </Box>
            <Box>
              <h3>Puntaje IA por archivos base</h3>
              <p>{puntaje_archivos ? `${puntaje_archivos} / 10` : 'Sin analizar'}</p>
            </Box>
            <Box>
              <h3>Visita a terreno</h3>
              <p>{visita_terreno ? visita_terreno : 'No obligatoria'}</p>
            </Box>
          </Box>
        </Box>
      </Box>
  );
};

export default Detail;
